import { Controller } from "@hotwired/stimulus"

export default class CoachSelectionController extends Controller {
  static readonly targets = [
    "profile",
    "loadMoreButton",
    "search",
    "language",
    "timezone",
    "emptyState",
  ]

  declare profileTargets: HTMLElement[];
  declare loadMoreButtonTarget: HTMLElement;
  declare hasLoadMoreButtonTarget: boolean;
  declare searchTarget: HTMLInputElement;
  declare languageTarget: HTMLSelectElement;
  declare timezoneTarget: HTMLSelectElement;
  declare emptyStateTarget: HTMLElement;

  perPage = Number.parseInt((this.data.get("perPageNumber") || "3"))
  hiddenCSSClass = '!hidden'
  initialVisibility = new Map<HTMLElement, boolean>()

  connect() {
    this.profileTargets.forEach(profile => {
      this.initialVisibility.set(profile, !profile.classList.contains(this.hiddenCSSClass))
    })
  }

  filterProfiles() {
    const search = this.searchTarget.value.trim().toLowerCase()
    const language = this.languageTarget.value.toLowerCase()
    const timezone = this.timezoneTarget.value.toLowerCase()
    const filtersActive = Boolean(search || language || timezone)

    if (!filtersActive) {
      this.restoreInitialVisibility()
      this.emptyStateTarget.classList.add(this.hiddenCSSClass)
      this.updateLoadMoreButton()
      return
    }

    let matches = 0
    this.profileTargets.forEach(profile => {
      const languages = (profile.dataset.coachLanguages || '').split('|')
      const matchesSearch = !search || (profile.dataset.coachName || '').includes(search)
      const matchesLanguage = !language || languages.includes(language)
      const matchesTimezone = !timezone || profile.dataset.coachTimezoneRegion === timezone
      const matchesFilters = matchesSearch && matchesLanguage && matchesTimezone

      profile.classList.toggle(this.hiddenCSSClass, !matchesFilters)
      if (matchesFilters) matches += 1
    })

    this.emptyStateTarget.classList.toggle(this.hiddenCSSClass, matches > 0)
    if (this.hasLoadMoreButtonTarget) this.loadMoreButtonTarget.classList.add(this.hiddenCSSClass)
  }

  loadMoreProfiles(event: Event) {
    event.preventDefault()
    this.showNextPageOfProfiles()
    if(this.noMoreProfiles) { this.hideLoadMoreButton() }
  }

  showNextPageOfProfiles() {
    this.nextPageOfProfiles.forEach(el => this.show(el))
  }

  get nextPageOfProfiles() {
    return this.hiddenProfiles.slice(0, this.perPage)
  }

  get hiddenProfiles() {
    return this.profileTargets.filter(el => el.classList.contains(this.hiddenCSSClass))
  }

  get noMoreProfiles() {
    return this.nextPageOfProfiles.length === 0
  }

  hideLoadMoreButton() {
    if (this.hasLoadMoreButtonTarget) this.loadMoreButtonTarget.classList.add(this.hiddenCSSClass)
  }

  show(profile: HTMLElement) {
    profile.classList.remove(this.hiddenCSSClass)
    this.initialVisibility.set(profile, true)
  }

  restoreInitialVisibility() {
    this.profileTargets.forEach(profile => {
      profile.classList.toggle(this.hiddenCSSClass, !this.initialVisibility.get(profile))
    })
  }

  updateLoadMoreButton() {
    if (!this.hasLoadMoreButtonTarget) return

    this.loadMoreButtonTarget.classList.toggle(this.hiddenCSSClass, this.hiddenProfiles.length === 0)
  }
}
